import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Service,
  Venue,
  AddOn,
  Appointment,
  Company,
  Customer,
  CheckoutPricing,
  BookingAvailabilitySlot
} from '@/types';
import {
  X,
  Search,
  User,
  Building2,
  Calendar,
  Clock,
  CheckCircle,
  PlusCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  CreditCard,
  Lock,
  Loader2,
  ShieldCheck
} from 'lucide-react';
import { COUNTRY_OPTIONS, getDefaultCountryCode } from '@/constants';
import { api } from '@/services/api';
import { tokenizeCardDetails } from '@/services/opayo';
import {
  filterPastAvailabilitySlots,
  getAvailableTimeSlotsForDate,
  isDateBlockedBySchedule
} from '@/lib/scheduling';
import {
  formatCardNumber,
  formatDobFromIso,
  formatDobInput,
  formatExpiry,
  normalizeDobToIso,
  isValidDob,
  isValidEmail
} from '@/lib/formatters';
import { buildMapById } from '@/lib/collections';
import { isValidUkPhone, normalizeUkPhone } from '@/lib/phone';
import { createId } from '@/lib/ids';
import { buildBrowserInfo } from '@/lib/opayo';
import { getTierPrice } from '@/lib/pricing';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface ManualBookingModalProps {
  onClose: () => void;
  onSave: (booking: Appointment) => void;
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  servicesById?: Map<string, Service>;
  venuesById?: Map<string, Venue>;
  addOnsById?: Map<string, AddOn>;
  customers: Customer[];
  companies: Company[];
  appointments: Appointment[];
}

type ThreeDSData = {
  acsUrl: string;
  cReq: string;
  termUrl: string;
  threeDSSessionData?: string;
};

const buildBookingRef = () => `K2-${createId().slice(0, 5).toUpperCase()}`;
const formatDobInputHyphen = (value: string) => formatDobInput(value).replace(/\//g, '-');
const formatDobFromIsoHyphen = (value: string) => formatDobFromIso(value).replace(/\//g, '-');
const normalizeDobToIsoHyphen = (value: string) => normalizeDobToIso(value.replace(/-/g, '/'));
const isValidDobHyphen = (value: string) => isValidDob(value.replace(/-/g, '/'));
const CUSTOMER_SEARCH_PAGE_SIZE = 100;
const MAX_CUSTOMER_SEARCH_PAGES = 200;
const normalizeSearchText = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase();
const getCustomerLookupKey = (customer: Customer) =>
  customer.id || normalizeSearchText(customer.email);

export const ManualBookingModal: React.FC<ManualBookingModalProps> = ({
  onClose,
  onSave,
  services,
  venues,
  addOns,
  servicesById: servicesByIdFromProps,
  venuesById: venuesByIdFromProps,
  addOnsById: addOnsByIdFromProps,
  customers,
  companies,
  appointments
}) => {
  const currentYear = new Date().getFullYear();
  const [threeDSData, setThreeDSData] = useState<ThreeDSData | null>(null);
  const [step, setStep] = useState(1);
  const [isCorporate, setIsCorporate] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'invoice'>('card');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<Company[]>([]);
  const [isCompanySearchLoading, setIsCompanySearchLoading] = useState(false);
  const [companySearchError, setCompanySearchError] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [allowOverbook, setAllowOverbook] = useState(false);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutPricing, setCheckoutPricing] = useState<CheckoutPricing | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | undefined>(undefined);
  const [pendingBookingRef, setPendingBookingRef] = useState<string | null>(null);
  const [availabilitySlots, setAvailabilitySlots] = useState<BookingAvailabilitySlot[]>([]);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [paymentResultModal, setPaymentResultModal] = useState<{
    type: 'success' | 'error';
    message: string;
    shouldFinalize: boolean;
  } | null>(null);

  // Card Details State
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    city: '',
    postalCode: '',
    country: getDefaultCountryCode()
  });

  // Customer Selection State
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [isCustomerSearchLoading, setIsCustomerSearchLoading] = useState(false);
  const [customerSearchError, setCustomerSearchError] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    dob: '',
    jobTitle: ''
  });

  const [dobTouched, setDobTouched] = useState(false);

  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  // Booking State
  const [bookingData, setBookingData] = useState({
    venueId: '',
    serviceId: '',
    addOnIds: [] as string[],
    date: null as Date | null,
    time: ''
  });
  const servicesById = useMemo(
    () => servicesByIdFromProps ?? buildMapById(services),
    [servicesByIdFromProps, services]
  );
  const addOnsById = useMemo(
    () => addOnsByIdFromProps ?? buildMapById(addOns),
    [addOnsByIdFromProps, addOns]
  );
  const venuesById = useMemo(
    () => venuesByIdFromProps ?? buildMapById(venues),
    [venuesByIdFromProps, venues]
  );
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const selectedCompany =
    companySearchResults.find((c) => c.id === selectedCompanyId) ||
    companies.find((c) => c.id === selectedCompanyId);

  const normalizedCustomerSearch = normalizeSearchText(customerSearch);
  const hasCustomerSearch = Boolean(normalizedCustomerSearch);
  const localCustomerMatches = hasCustomerSearch
    ? customers.filter((customer) => {
        const name = normalizeSearchText(customer.name);
        const email = normalizeSearchText(customer.email);
        return name.includes(normalizedCustomerSearch) || email.includes(normalizedCustomerSearch);
      })
    : [];
  const filteredCustomers = hasCustomerSearch
    ? (() => {
        const merged = new Map<string, Customer>();
        localCustomerMatches.forEach((customer) => {
          merged.set(getCustomerLookupKey(customer), customer);
        });
        customerSearchResults.forEach((customer) => {
          merged.set(getCustomerLookupKey(customer), customer);
        });
        return Array.from(merged.values())
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
          .slice(0, 10);
      })()
    : [];

  const eligibleCompanies = [...companies]
    .filter((company) => company.status === 'active' || company.status === 'on_hold')
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const normalizedCompanySearch = companySearch.trim().toLowerCase();
  const hasCompanySearch = Boolean(normalizedCompanySearch);
  const localCompanyMatches = hasCompanySearch
    ? eligibleCompanies.filter((company) => {
        return (
          company.name.toLowerCase().includes(normalizedCompanySearch) ||
          company.email.toLowerCase().includes(normalizedCompanySearch)
        );
      })
    : [];
  const filteredCompanies = hasCompanySearch
    ? (() => {
        const merged = new Map<string, Company>();
        localCompanyMatches.forEach((company) => merged.set(company.id, company));
        companySearchResults.forEach((company) => merged.set(company.id, company));
        return Array.from(merged.values()).sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      })()
    : (() => {
        if (!selectedCompany) return eligibleCompanies;
        if (eligibleCompanies.some((company) => company.id === selectedCompany.id)) {
          return eligibleCompanies;
        }
        return [...eligibleCompanies, selectedCompany].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
      })();

  const handleSelectExistingCustomer = useCallback((c: Customer) => {
    setSelectedCustomerId(c.id);
    setCustomerDetails({
      name: c.name,
      email: c.email,
      phone: c.phone || '',
      dob: formatDobFromIsoHyphen(c.dob || ''),
      jobTitle: c.jobTitle || ''
    });
    setCustomerSearch('');
  }, []);

  const handleCreateNewCustomer = () => {
    setSelectedCustomerId(null);
    setCustomerDetails({
      name: '',
      email: '',
      phone: '',
      dob: '',
      jobTitle: ''
    });
  };

  useEffect(() => {
    const term = customerSearch.trim();
    if (!term || selectedCustomerId) {
      setCustomerSearchResults([]);
      setCustomerSearchError('');
      setIsCustomerSearchLoading(false);
      return;
    }

    let isActive = true;
    setIsCustomerSearchLoading(true);
    setCustomerSearchError('');
    setCustomerSearchResults([]);

    const timeoutId = window.setTimeout(() => {
      if (!isActive) return;
      const loadCustomerSearchResults = async () => {
        try {
          const merged = new Map<string, Customer>();
          let page = 1;
          let totalPages = 1;

          while (isActive && page <= totalPages && page <= MAX_CUSTOMER_SEARCH_PAGES) {
            const data = await api.listStaffCustomers({
              page,
              pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
              q: term,
              sortBy: 'name',
              sortDir: 'asc'
            });
            if (!isActive) return;

            const items = data.items || [];
            const totalCount = Number(data.totalCount || 0);
            totalPages = Math.max(1, Math.ceil(totalCount / CUSTOMER_SEARCH_PAGE_SIZE));
            if (!items.length) break;

            items.forEach((customer) => {
              merged.set(getCustomerLookupKey(customer), customer);
            });
            page += 1;
          }

          if (!isActive) return;
          setCustomerSearchResults(
            Array.from(merged.values()).sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            )
          );
        } catch (err: unknown) {
          if (!isActive) return;
          setCustomerSearchResults([]);
          setCustomerSearchError(normalizeErrorMessage(err, 'Failed to search patients.'));
        } finally {
          if (isActive) setIsCustomerSearchLoading(false);
        }
      };
      loadCustomerSearchResults();
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [customerSearch, selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) return;
    const normalizedEmail = normalizeSearchText(customerDetails.email);
    if (!normalizedEmail) return;

    const knownCustomers = [...customers, ...customerSearchResults];
    const matchedCustomer = knownCustomers.find(
      (customer) => normalizeSearchText(customer.email) === normalizedEmail
    );
    if (matchedCustomer) {
      handleSelectExistingCustomer(matchedCustomer);
      return;
    }

    if (!normalizedEmail.includes('@')) return;

    let isActive = true;
    const lookupExactCustomer = async () => {
      try {
        const data = await api.listStaffCustomers({
          page: 1,
          pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
          q: normalizedEmail,
          sortBy: 'name',
          sortDir: 'asc'
        });
        if (!isActive) return;
        const exactMatch = (data.items || []).find(
          (customer) => normalizeSearchText(customer.email) === normalizedEmail
        );
        if (exactMatch) {
          handleSelectExistingCustomer(exactMatch);
        }
      } catch {
        // Ignore lookup errors here; explicit search UI already surfaces errors.
      }
    };
    lookupExactCustomer();

    return () => {
      isActive = false;
    };
  }, [
    customerDetails.email,
    customers,
    customerSearchResults,
    selectedCustomerId,
    handleSelectExistingCustomer
  ]);

  useEffect(() => {
    const term = companySearch.trim();
    if (!term) {
      setCompanySearchResults((prev) => {
        const selected = prev.find((c) => c.id === selectedCompanyId);
        return selected ? [selected] : [];
      });
      setCompanySearchError('');
      setIsCompanySearchLoading(false);
      return;
    }

    let isActive = true;
    setIsCompanySearchLoading(true);
    setCompanySearchError('');
    setCompanySearchResults([]);

    const timeoutId = window.setTimeout(() => {
      if (!isActive) return;
      api
        .listStaffCompanies({
          page: 1,
          pageSize: 50,
          q: term,
          status: 'active,on_hold',
          sortBy: 'name',
          sortDir: 'asc'
        })
        .then((data) => {
          if (!isActive) return;
          setCompanySearchResults(data.items || []);
        })
        .catch((err: unknown) => {
          if (!isActive) return;
          setCompanySearchResults([]);
          setCompanySearchError(normalizeErrorMessage(err, 'Failed to search companies.'));
        })
        .finally(() => {
          if (isActive) setIsCompanySearchLoading(false);
        });
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [companySearch, selectedCompanyId]);

  const getPrice = useCallback(
    (item: Service | AddOn) =>
      getTierPrice(item, { company: selectedCompany || null, isCorporate }),
    [isCorporate, selectedCompany]
  );
  const selectedService = bookingData.serviceId
    ? servicesById.get(bookingData.serviceId)
    : undefined;
  const selectedServicePrice = selectedService != null ? Number(getPrice(selectedService)) : null;
  const requiresAddOnSelection = Boolean(
    selectedService &&
    selectedServicePrice != null &&
    selectedServicePrice <= 0 &&
    selectedService.name.toLowerCase().includes('health surveillance')
  );
  const availableAddOns = useMemo(
    () =>
      bookingData.serviceId
        ? addOns.filter((addon) => addon.compatibleServiceIds.includes(bookingData.serviceId))
        : [],
    [addOns, bookingData.serviceId]
  );
  const compatibleAddOnIds = useMemo(
    () => new Set(availableAddOns.map((addon) => addon.id)),
    [availableAddOns]
  );
  const hasCompatibleAddOn = bookingData.addOnIds.some((id) => compatibleAddOnIds.has(id));
  const isAddOnSelectionMissing = requiresAddOnSelection && !hasCompatibleAddOn;

  const getPricingForCheckout = useCallback((): CheckoutPricing => {
    if (checkoutPricing) return checkoutPricing;
    let subtotalAmount = 0;
    const service = bookingData.serviceId ? servicesById.get(bookingData.serviceId) : undefined;
    if (service) subtotalAmount += getPrice(service);
    bookingData.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) subtotalAmount += getPrice(addon);
    });
    return {
      currency: 'GBP',
      subtotalAmount,
      vatRatePercent: 0,
      vatAmount: 0,
      totalAmount: subtotalAmount
    };
  }, [
    checkoutPricing,
    bookingData.serviceId,
    bookingData.addOnIds,
    servicesById,
    addOnsById,
    getPrice
  ]);

  const finalizeBooking = useCallback(() => {
    const bookingRef = pendingBookingRef || buildBookingRef();
    const pricing = getPricingForCheckout();
    const phoneValue = customerDetails.phone.trim();
    const hasQuestionnaire =
      Boolean(
        bookingData.serviceId
          ? servicesById.get(bookingData.serviceId)?.questionnaireConfig?.templateId
          : false
      ) || bookingData.addOnIds.some((id) => addOnsById.get(id)?.questionnaireConfig?.templateId);
    const appointment: Appointment = {
      id: bookingRef,
      venueId: bookingData.venueId,
      serviceId: bookingData.serviceId,
      addOnIds: bookingData.addOnIds,
      date: bookingData.date!.toISOString(),
      time: bookingData.time,
      customerName: customerDetails.name,
      customerEmail: customerDetails.email,
      customerPhone: phoneValue ? normalizeUkPhone(phoneValue) : '',
      customerDob: normalizeDobToIsoHyphen(customerDetails.dob),
      customerJobTitle: customerDetails.jobTitle,
      allowOverbook: allowOverbook || undefined,
      companyId: isCorporate ? selectedCompanyId : undefined,
      poNumber: isCorporate ? poNumber : undefined,
      paymentMethod: paymentMethod,
      paymentId: pendingPaymentId,
      subtotalAmount: pricing.subtotalAmount,
      vatRatePercent: pricing.vatRatePercent,
      vatAmount: pricing.vatAmount,
      totalAmount: pricing.totalAmount,
      currency: pricing.currency,
      status: 'booked',
      createdAt: new Date().toISOString(),
      questionnaireStatus: hasQuestionnaire ? 'pending' : 'not_required'
    };

    onSave(appointment);
    setPendingPaymentId(undefined);
    setPendingBookingRef(null);
    setIsProcessing(false);
    onClose();
  }, [
    bookingData,
    customerDetails,
    isCorporate,
    selectedCompanyId,
    paymentMethod,
    pendingPaymentId,
    pendingBookingRef,
    poNumber,
    allowOverbook,
    services,
    addOns,
    getPricingForCheckout,
    servicesById,
    addOnsById,
    onSave,
    onClose
  ]);

  const toggleAddOn = (id: string) => {
    setBookingData((prev) => ({
      ...prev,
      addOnIds: prev.addOnIds.includes(id)
        ? prev.addOnIds.filter((aid) => aid !== id)
        : [...prev.addOnIds, id]
    }));
  };

  useEffect(() => {
    if (!bookingData.serviceId) {
      setCheckoutPricing(null);
      return;
    }

    let isCurrent = true;
    api
      .getCheckoutQuote({
        serviceId: bookingData.serviceId,
        addOnIds: bookingData.addOnIds,
        companyId: isCorporate ? selectedCompanyId : undefined,
        currency: 'GBP'
      })
      .then((result) => {
        if (isCurrent) setCheckoutPricing(result.pricing);
      })
      .catch(() => {
        if (isCurrent) setCheckoutPricing(null);
      });

    return () => {
      isCurrent = false;
    };
  }, [bookingData.serviceId, bookingData.addOnIds, isCorporate, selectedCompanyId]);

  useEffect(() => {
    if (!bookingData.date || !bookingData.venueId || !bookingData.serviceId) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingAvailability(true);
    setHasAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: bookingData.venueId,
        date: bookingData.date.toISOString(),
        serviceId: bookingData.serviceId,
        addOnIds: bookingData.addOnIds
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(bookingData.date, result?.slots || []);
        setAvailabilitySlots(slots);
        setHasAvailabilityData(true);
        setBookingData((prev) => {
          if (!prev.time) return prev;
          const selectedSlot = slots.find((slot) => slot.time === prev.time);
          if (!selectedSlot) {
            return { ...prev, time: '' };
          }
          if (selectedSlot.available || allowOverbook) {
            return prev;
          }
          return { ...prev, time: '' };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setAvailabilitySlots([]);
        setHasAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingAvailability(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [
    bookingData.date,
    bookingData.venueId,
    bookingData.serviceId,
    bookingData.addOnIds,
    allowOverbook
  ]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'opayo:3ds') return;
      setThreeDSData(null);
      if (!event.data.success) {
        const message = event.data.statusDetail || '3DS verification failed.';
        setError(message);
        setIsProcessing(false);
        setPaymentResultModal({ type: 'error', message, shouldFinalize: false });
        return;
      }
      if (event.data.paymentId) {
        setPendingPaymentId(event.data.paymentId);
      }
      setPaymentResultModal({
        type: 'success',
        message: 'Payment verified successfully.',
        shouldFinalize: true
      });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (!threeDSData) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = threeDSData.acsUrl;
    form.target = 'opayo-3ds-iframe';
    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('creq', threeDSData.cReq);
    addField('TermUrl', threeDSData.termUrl);
    if (threeDSData.threeDSSessionData) {
      addField('threeDSSessionData', threeDSData.threeDSSessionData);
    }
    document.body.appendChild(form);
    form.submit();
    return () => {
      document.body.removeChild(form);
    };
  }, [threeDSData]);

  const handleSubmit = async () => {
    setError('');
    if (isAddOnSelectionMissing) {
      setError('Please select at least one add-on for Health Surveillance bookings.');
      return;
    }
    const cleanCardNumber = cardDetails.number.replace(/\D/g, '');
    const cleanCvc = cardDetails.cvc.replace(/\D/g, '');
    if (!customerDetails.name.trim() || !customerDetails.email.trim()) {
      setError('Please enter the patient name and email.');
      return;
    }
    if (!isDobValid) {
      setDobTouched(true);
      setError(
        `Please enter a valid date of birth in DD-MM-YYYY format (year 1900-${currentYear}).`
      );
      return;
    }
    if (!isEmailValid) {
      setEmailTouched(true);
      setError('Please enter a valid email address.');
      return;
    }
    if (!isPhoneValid) {
      setPhoneTouched(true);
      setError('Please enter a valid UK phone number.');
      return;
    }

    // Validation for Mandatory PO
    if (isCorporate && selectedCompany?.requirePo && !poNumber.trim()) {
      setError(`Company "${selectedCompany.name}" requires a PO Number for all bookings.`);
      return;
    }

    if (paymentMethod === 'card') {
      if (!cardDetails.cardholderName.trim()) {
        setError('Please enter the cardholder name.');
        return;
      }
      if (
        cleanCardNumber.length < 12 ||
        cleanCardNumber.length > 19 ||
        cardDetails.expiry.length < 5 ||
        cleanCvc.length < 3 ||
        cleanCvc.length > 4
      ) {
        setError('Please enter valid credit card details.');
        return;
      }
      if (
        !billingAddress.address1.trim() ||
        !billingAddress.city.trim() ||
        !billingAddress.postalCode.trim() ||
        !billingAddress.country.trim()
      ) {
        setError('Please enter billing address details.');
        return;
      }
      if (!/^[A-Za-z]{2}$/.test(billingAddress.country.trim())) {
        setError('Please select a valid billing country.');
        return;
      }
    }

    if (!bookingData.date) return;

    if (paymentMethod === 'card') {
      setIsProcessing(true);
      if (!bookingData.serviceId) {
        setError('Please select a service.');
        setIsProcessing(false);
        return;
      }
      const bookingRef = pendingBookingRef || buildBookingRef();
      setPendingBookingRef(bookingRef);
      try {
        const quote = await api.getCheckoutQuote({
          serviceId: bookingData.serviceId,
          addOnIds: bookingData.addOnIds,
          companyId: isCorporate ? selectedCompanyId : undefined,
          currency: 'GBP'
        });
        setCheckoutPricing(quote.pricing);
        const totalAmount = quote.pricing.totalAmount;
        if (totalAmount > 0) {
          const expiryDigits = cardDetails.expiry.replace(/\D/g, '').slice(0, 4);
          const nameParts = customerDetails.name.trim().split(/\s+/).filter(Boolean);
          const firstName = nameParts[0] || 'Customer';
          const lastName = nameParts.slice(1).join(' ') || 'Customer';
          const session = await api.createOpayoSessionKey();
          const cardIdentifier = await tokenizeCardDetails({
            session,
            cardDetails: {
              number: cleanCardNumber,
              expiry: expiryDigits,
              cvc: cardDetails.cvc,
              cardholderName:
                cardDetails.cardholderName.trim() || customerDetails.name.trim() || 'Customer'
            }
          });

          const paymentResult = await api.processOpayoPayment({
            amount: totalAmount,
            expectedTotal: totalAmount,
            currency: 'GBP',
            description: `Manual Booking - ${selectedService?.name || 'Appointment'}`,
            merchantSessionKey: session.merchantSessionKey,
            cardIdentifier,
            billingAddress: {
              address1: billingAddress.address1.trim(),
              city: billingAddress.city.trim(),
              postalCode: billingAddress.postalCode.trim(),
              country: billingAddress.country.trim().toUpperCase()
            },
            customer: {
              firstName,
              lastName,
              email: customerDetails.email.trim(),
              phone: phoneValue ? normalizeUkPhone(phoneValue) : ''
            },
            browser: buildBrowserInfo(),
            serviceId: bookingData.serviceId,
            addOnIds: bookingData.addOnIds,
            companyId: isCorporate ? selectedCompanyId : undefined,
            appointmentId: bookingRef
          });
          if (paymentResult?.paymentId) {
            setPendingPaymentId(paymentResult.paymentId);
          }
          if (
            paymentResult?.requires3DS &&
            paymentResult.acsUrl &&
            paymentResult.cReq &&
            paymentResult.termUrl
          ) {
            setThreeDSData({
              acsUrl: paymentResult.acsUrl,
              cReq: paymentResult.cReq,
              termUrl: paymentResult.termUrl,
              threeDSSessionData: paymentResult.threeDSSessionData
            });
            return;
          }
          setPaymentResultModal({
            type: 'success',
            message: 'Payment completed successfully.',
            shouldFinalize: true
          });
          return;
        }
      } catch (err: unknown) {
        const message = normalizeErrorMessage(err, 'Payment failed. Please try again.');
        setError(message);
        setPaymentResultModal({ type: 'error', message, shouldFinalize: false });
        setIsProcessing(false);
        return;
      }
    }
    finalizeBooking();
  };

  const dobValue = customerDetails.dob.trim();
  const isDobValid = isValidDobHyphen(dobValue);
  const showDobError = dobTouched && !isDobValid;

  const emailValue = customerDetails.email.trim();
  const isEmailValid = isValidEmail(emailValue);
  const showEmailError = emailTouched && !isEmailValid;

  const phoneValue = customerDetails.phone.trim();
  const isPhoneValid = !phoneValue || isValidUkPhone(phoneValue);
  const showPhoneError = phoneTouched && !isPhoneValid;

  const canContinueToBooking = Boolean(
    bookingData.venueId &&
    bookingData.serviceId &&
    bookingData.date &&
    bookingData.time &&
    !isAddOnSelectionMissing
  );
  const canSubmit = Boolean(
    bookingData.venueId &&
    bookingData.serviceId &&
    bookingData.date &&
    bookingData.time &&
    customerDetails.name &&
    customerDetails.email &&
    customerDetails.dob &&
    (isCorporate ? selectedCompanyId : true) &&
    !isAddOnSelectionMissing
  );

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const minBookableDate = new Date(today);
  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const dates = Array.from({ length: monthEnd.getDate() }, (_, i) => {
    return new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), i + 1);
  });
  const leadingEmptyDays = monthStart.getDay();
  const minMonthStart = new Date(minBookableDate.getFullYear(), minBookableDate.getMonth(), 1);
  const canGoPrevMonth = monthStart.getTime() > minMonthStart.getTime();
  const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const handleMonthChange = (offset: number) => {
    const nextMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1);
    if (offset < 0 && nextMonth.getTime() < minMonthStart.getTime()) return;
    setVisibleMonth(nextMonth);
    setBookingData((prev) => {
      if (!prev.date) return prev;
      const isDateInVisibleMonth =
        prev.date.getFullYear() === nextMonth.getFullYear() &&
        prev.date.getMonth() === nextMonth.getMonth();
      return isDateInVisibleMonth ? prev : { ...prev, date: null, time: '' };
    });
  };

  const getMinutes = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const getTotalDuration = () => {
    let duration = selectedService?.durationMinutes || 30;
    bookingData.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) duration += addon.durationMinutes || 0;
    });
    return duration;
  };

  const selectedVenue = bookingData.venueId ? venuesById.get(bookingData.venueId) : undefined;
  const requiredDuration = getTotalDuration();
  const generatedSlots = bookingData.date
    ? getAvailableTimeSlotsForDate(bookingData.date, selectedVenue?.schedule, {
        requiredDurationMinutes: requiredDuration
      })
    : [];
  const availabilityByTime = new Map(availabilitySlots.map((slot) => [slot.time, slot]));
  const availableSlots = bookingData.date
    ? hasAvailabilityData
      ? availabilitySlots.map((slot) => slot.time)
      : generatedSlots
    : [];
  const fallbackBusyIntervals = !hasAvailabilityData
    ? appointments
        .filter(
          (app) =>
            app.venueId === bookingData.venueId &&
            new Date(app.date).toDateString() === bookingData.date?.toDateString() &&
            app.status !== 'cancelled'
        )
        .map((app) => {
          const appService = servicesById.get(app.serviceId);
          let appDuration = appService?.durationMinutes || 30;
          app.addOnIds.forEach((aid) => {
            const addon = addOnsById.get(aid);
            if (addon) appDuration += addon.durationMinutes || 0;
          });
          const start = getMinutes(app.time);
          return { start, end: start + appDuration };
        })
    : [];
  const pricing = getPricingForCheckout();
  const closePaymentResultModal = () => {
    const shouldFinalize = Boolean(paymentResultModal?.shouldFinalize);
    setPaymentResultModal(null);
    if (shouldFinalize) {
      finalizeBooking();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 overflow-y-auto">
      {paymentResultModal && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center border border-slate-200">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                paymentResultModal.type === 'success'
                  ? 'bg-green-50 text-green-600'
                  : 'bg-red-50 text-red-600'
              }`}
            >
              {paymentResultModal.type === 'success' ? (
                <CheckCircle className="w-8 h-8" />
              ) : (
                <AlertCircle className="w-8 h-8" />
              )}
            </div>
            <h4 className="text-lg font-bold text-slate-900 mb-2">
              {paymentResultModal.type === 'success' ? 'Payment Successful' : 'Payment Failed'}
            </h4>
            <p className="text-slate-600 text-sm mb-6">{paymentResultModal.message}</p>
            <button
              onClick={closePaymentResultModal}
              className={`w-full py-2.5 rounded-xl text-sm font-bold text-white ${
                paymentResultModal.type === 'success'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {paymentResultModal.type === 'success' ? 'Continue' : 'Close'}
            </button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-slate-800">New Manual Booking</h3>
            <p className="text-xs text-slate-500">
              Creating a record from phone or offline request
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 2 ? (
            <div className="space-y-6">
              {/* Customer Selection */}
              <div className="max-w-2xl mx-auto">
                <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" /> Patient Selection
                </label>
                {!selectedCustomerId ? (
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-3 text-slate-400 w-5 h-5" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-3 border rounded-xl outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 text-sm"
                      placeholder="Search existing patient by name or email..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                    {customerSearch && (
                      <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-xl mt-1 z-20 overflow-hidden divide-y">
                        {filteredCustomers.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => handleSelectExistingCustomer(c)}
                            className="w-full p-3 text-left hover:bg-teal-50 flex justify-between items-center transition-colors"
                          >
                            <div>
                              <div className="font-bold text-sm text-slate-800">{c.name}</div>
                              <div className="text-xs text-slate-500">{c.email}</div>
                            </div>
                            <CheckCircle className="w-4 h-4 text-teal-500 opacity-0 group-hover:opacity-100" />
                          </button>
                        ))}
                        {isCustomerSearchLoading && (
                          <div className="p-3 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Searching patients...
                          </div>
                        )}
                        {customerSearchError && (
                          <div className="p-3 text-center text-xs text-red-600">
                            {customerSearchError}
                          </div>
                        )}
                        {!isCustomerSearchLoading &&
                          !customerSearchError &&
                          filteredCustomers.length === 0 && (
                            <div className="p-4 text-center text-sm text-slate-400">
                              No matches found.
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-teal-50 border border-teal-100 p-4 rounded-xl flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-teal-100 p-2 rounded-full text-teal-600">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">{customerDetails.name}</div>
                        <div className="text-xs text-slate-500">{customerDetails.email}</div>
                      </div>
                    </div>
                    <button
                      onClick={handleCreateNewCustomer}
                      className="text-xs font-bold text-teal-600 hover:underline"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* Patient Detail Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Full Name
                    </label>
                    <input
                      className="w-full border rounded-lg p-2.5 text-sm bg-white"
                      value={customerDetails.name}
                      onChange={(e) =>
                        setCustomerDetails({ ...customerDetails, name: e.target.value })
                      }
                      disabled={!!selectedCustomerId}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="w-full border rounded-lg p-2.5 text-sm bg-white"
                      value={customerDetails.email}
                      onChange={(e) =>
                        setCustomerDetails({ ...customerDetails, email: e.target.value })
                      }
                      onBlur={() => setEmailTouched(true)}
                      disabled={!!selectedCustomerId}
                    />
                    {showEmailError && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        Enter a valid email address.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Phone Number (optional)
                    </label>
                    <input
                      className="w-full border rounded-lg p-2.5 text-sm bg-white"
                      value={customerDetails.phone}
                      onChange={(e) =>
                        setCustomerDetails({ ...customerDetails, phone: e.target.value })
                      }
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        setPhoneTouched(true);
                        if (value && isValidUkPhone(value)) {
                          setCustomerDetails({
                            ...customerDetails,
                            phone: normalizeUkPhone(value)
                          });
                        }
                      }}
                      disabled={!!selectedCustomerId}
                    />
                    <p className="mt-1 text-[10px] text-slate-400">
                      UK numbers only, e.g. 07123 456789 or +44 7123 456789.
                    </p>
                    {showPhoneError && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        Enter a valid UK phone number.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="DD-MM-YYYY"
                      maxLength={10}
                      className="w-full border rounded-lg p-2.5 text-sm bg-white"
                      value={customerDetails.dob}
                      onChange={(e) =>
                        setCustomerDetails({
                          ...customerDetails,
                          dob: formatDobInputHyphen(e.target.value)
                        })
                      }
                      onBlur={() => setDobTouched(true)}
                    />
                    {showDobError && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        Use DD-MM-YYYY. Year must be between 1900 and {currentYear}.
                      </p>
                    )}
                  </div>
                </div>

                {/* Booking Type & Payment Toggle */}
                <div className="pt-6 mt-6 border-t space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Booking Type
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => {
                          setIsCorporate(false);
                          setPaymentMethod('card');
                        }}
                        className={`p-4 border-2 rounded-xl text-center transition-all ${!isCorporate ? 'border-teal-500 bg-teal-50 text-teal-700 font-bold' : 'border-slate-200 text-slate-500'}`}
                      >
                        Private Booking
                      </button>
                      <button
                        onClick={() => {
                          setIsCorporate(true);
                          setPaymentMethod('invoice');
                        }}
                        className={`p-4 border-2 rounded-xl text-center transition-all ${isCorporate ? 'border-blue-500 bg-blue-50 text-blue-700 font-bold' : 'border-slate-200 text-slate-500'}`}
                      >
                        Corporate Booking
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Payment Method
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setPaymentMethod('card')}
                        className={`p-3 border-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'card' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-slate-100 text-slate-500'}`}
                      >
                        <CreditCard className="w-4 h-4" /> Credit / Debit Card
                      </button>
                      <button
                        onClick={() => setPaymentMethod('invoice')}
                        className={`p-3 border-2 rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${paymentMethod === 'invoice' ? 'border-purple-500 bg-purple-50 text-purple-700 font-bold' : 'border-slate-100 text-slate-500'}`}
                      >
                        <FileText className="w-4 h-4" /> Raise Invoice
                      </button>
                    </div>
                  </div>

                  {isCorporate && (
                    <div className="mt-4 animate-fade-in grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/50 p-4 rounded-2xl border border-blue-100">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                          Company
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-3 text-slate-400 w-4 h-4" />
                          <input
                            type="text"
                            className="w-full pl-9 pr-3 py-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                            placeholder="Search and select company..."
                            value={companySearch}
                            onFocus={() => setIsCompanyDropdownOpen(true)}
                            onBlur={() => setIsCompanyDropdownOpen(false)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCompanySearch(value);
                              setIsCompanyDropdownOpen(true);
                              if (!value.trim()) {
                                setSelectedCompanyId('');
                              } else if (selectedCompanyId && selectedCompany) {
                                const normalized = value.trim().toLowerCase();
                                if (normalized !== selectedCompany.name.toLowerCase()) {
                                  setSelectedCompanyId('');
                                }
                              }
                            }}
                          />
                          {isCompanyDropdownOpen && (
                            <div className="absolute top-full left-0 w-full bg-white border rounded-xl shadow-xl mt-1 z-30 overflow-hidden divide-y max-h-56 overflow-y-auto">
                              {isCompanySearchLoading ? (
                                <div className="p-3 text-sm text-slate-500">
                                  Searching companies...
                                </div>
                              ) : companySearchError ? (
                                <div className="p-3 text-sm text-red-500">{companySearchError}</div>
                              ) : filteredCompanies.length === 0 ? (
                                <div className="p-3 text-sm text-slate-400">
                                  No matching companies found.
                                </div>
                              ) : (
                                filteredCompanies.slice(0, 15).map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onMouseDown={() => {
                                      setSelectedCompanyId(c.id);
                                      setCompanySearch(c.name);
                                      setIsCompanyDropdownOpen(false);
                                    }}
                                    className="w-full p-3 text-left hover:bg-blue-50 flex items-center justify-between transition-colors"
                                  >
                                    <div>
                                      <div className="font-bold text-sm text-slate-800">
                                        {c.name}
                                        {c.status === 'on_hold' ? ' (On Hold)' : ''}
                                      </div>
                                      <div className="text-xs text-slate-500">{c.email}</div>
                                    </div>
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {selectedCompany?.requirePo
                            ? 'PO Number (Required)'
                            : 'PO Number (Optional)'}
                        </label>
                        <input
                          placeholder={
                            selectedCompany?.requirePo
                              ? 'Required PO Reference'
                              : 'Optional Reference'
                          }
                          className={`w-full border rounded-lg p-2.5 text-sm transition-all ${selectedCompany?.requirePo && !poNumber ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}
                          value={poNumber}
                          onChange={(e) => setPoNumber(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {paymentMethod === 'card' && bookingData.serviceId && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
                      <div className="relative z-10 mb-4 space-y-2">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-teal-400 flex items-center gap-2">
                          <Lock className="w-3 h-3" /> Secure Payment
                        </h4>
                        <div className="flex items-center gap-1 text-[8px] font-black text-slate-400 bg-white/5 px-1.5 py-0.5 rounded w-fit">
                          <ShieldCheck className="w-2.5 h-2.5 text-teal-500" />
                          OPAYO SECURE
                        </div>
                      </div>
                      <div className="relative z-10 space-y-4">
                        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                          <div className="text-[10px] uppercase font-bold text-slate-400 mb-2">
                            Manual Transaction Entry
                          </div>
                          <div className="space-y-3">
                            <input
                              type="text"
                              className="w-full bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                              placeholder="Card Number"
                              maxLength={19}
                              value={cardDetails.number}
                              onChange={(e) =>
                                setCardDetails({
                                  ...cardDetails,
                                  number: formatCardNumber(e.target.value, 16)
                                })
                              }
                            />
                            <input
                              type="text"
                              className="w-full bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                              placeholder="Cardholder Name"
                              maxLength={50}
                              value={cardDetails.cardholderName}
                              onChange={(e) =>
                                setCardDetails({
                                  ...cardDetails,
                                  cardholderName: e.target.value
                                })
                              }
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                className="bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                                placeholder="MM / YY"
                                maxLength={5}
                                value={cardDetails.expiry}
                                onChange={(e) =>
                                  setCardDetails({
                                    ...cardDetails,
                                    expiry: formatExpiry(e.target.value)
                                  })
                                }
                              />
                              <input
                                type="text"
                                className="bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                                placeholder="CVC"
                                maxLength={4}
                                value={cardDetails.cvc}
                                onChange={(e) =>
                                  setCardDetails({
                                    ...cardDetails,
                                    cvc: e.target.value.replace(/[^0-9]/g, '')
                                  })
                                }
                              />
                            </div>
                            <input
                              type="text"
                              className="w-full bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                              placeholder="Billing Address"
                              value={billingAddress.address1}
                              onChange={(e) =>
                                setBillingAddress({ ...billingAddress, address1: e.target.value })
                              }
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                className="bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                                placeholder="City"
                                value={billingAddress.city}
                                onChange={(e) =>
                                  setBillingAddress({ ...billingAddress, city: e.target.value })
                                }
                              />
                              <input
                                type="text"
                                className="bg-white/10 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                                placeholder="Postcode"
                                value={billingAddress.postalCode}
                                onChange={(e) =>
                                  setBillingAddress({
                                    ...billingAddress,
                                    postalCode: e.target.value
                                  })
                                }
                              />
                            </div>
                            <select
                              className="w-full bg-slate-900 h-10 rounded border border-white/20 flex items-center px-3 text-white text-xs outline-none focus:border-teal-500 transition-colors"
                              style={{ colorScheme: 'dark' }}
                              value={billingAddress.country}
                              onChange={(e) =>
                                setBillingAddress({ ...billingAddress, country: e.target.value })
                              }
                            >
                              {COUNTRY_OPTIONS.map((option) => (
                                <option
                                  key={option.code}
                                  value={option.code}
                                  style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}
                                >
                                  {option.label} ({option.code})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-white/10 space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-400">Subtotal</span>
                            <span className="font-bold text-white">
                              £{pricing.subtotalAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-medium text-slate-400">
                              VAT ({pricing.vatRatePercent.toFixed(2)}%)
                            </span>
                            <span className="font-bold text-white">
                              £{pricing.vatAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-white/10">
                            <span className="text-sm font-medium text-slate-400">Total</span>
                            <span className="text-2xl font-black text-white">
                              £{pricing.totalAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Selection Panel */}
              <div className="lg:w-1/3 space-y-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Location / Venue
                  </label>
                  <select
                    className="w-full border rounded-lg p-2.5 text-sm bg-white"
                    value={bookingData.venueId}
                    onChange={(e) =>
                      setBookingData({
                        ...bookingData,
                        venueId: e.target.value,
                        date: null,
                        time: ''
                      })
                    }
                  >
                    <option value="">Select Venue</option>
                    {venues.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Main Service
                  </label>
                  <select
                    className="w-full border rounded-lg p-2.5 text-sm bg-white"
                    value={bookingData.serviceId}
                    onChange={(e) =>
                      setBookingData({ ...bookingData, serviceId: e.target.value, time: '' })
                    }
                  >
                    <option value="">Select Service</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} (£{getPrice(s).toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
                {/* Add-ons */}
                {bookingData.serviceId && (
                  <div className="pt-4 border-t">
                    <label className="block text-sm font-bold text-slate-700 mb-3">
                      {requiresAddOnSelection ? 'Add-ons (required)' : 'Optional Add-ons'}
                    </label>
                    <div className="space-y-2">
                      {availableAddOns.map((addon) => (
                        <label
                          key={addon.id}
                          className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-colors ${bookingData.addOnIds.includes(addon.id) ? 'bg-teal-50 border-teal-400' : 'hover:bg-slate-50'}`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-teal-600 rounded"
                              checked={bookingData.addOnIds.includes(addon.id)}
                              onChange={() => toggleAddOn(addon.id)}
                            />
                            <span className="text-sm font-medium text-slate-700">{addon.name}</span>
                          </div>
                          <span className="text-xs text-slate-500">
                            +£{getPrice(addon).toFixed(2)}
                          </span>
                        </label>
                      ))}
                      {isAddOnSelectionMissing && (
                        <p className="text-xs font-semibold text-amber-600">
                          Select at least one add-on to continue with Health Surveillance.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Calendar Panel */}
              <div className="flex-1 bg-slate-50 p-6 rounded-2xl border border-slate-200">
                {!bookingData.venueId ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 text-center">
                    <Calendar className="w-12 h-12 mb-2 opacity-20" />
                    <p>Please select a location first to view availability.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <h4 className="font-bold text-slate-700 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Select Date
                        </h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleMonthChange(-1)}
                            disabled={!canGoPrevMonth}
                            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                            aria-label="Previous month"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <div className="text-sm font-bold text-slate-800 min-w-[140px] text-center">
                            {visibleMonth.toLocaleDateString('en-GB', {
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                          <button
                            onClick={() => handleMonthChange(1)}
                            className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 flex items-center justify-center"
                            aria-label="Next month"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-2 mb-2">
                        {weekdayLabels.map((label) => (
                          <div
                            key={label}
                            className="text-[10px] font-bold text-slate-400 text-center uppercase"
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-2">
                        {Array.from({ length: leadingEmptyDays }).map((_, idx) => (
                          <div key={`empty-${idx}`} />
                        ))}
                        {dates.map((date) => {
                          const isSelected =
                            bookingData.date?.toDateString() === date.toDateString();
                          const { blocked, reason } = isDateBlockedBySchedule(
                            date,
                            selectedVenue?.schedule
                          );
                          const beforeBookingWindow = date.getTime() < minBookableDate.getTime();
                          const isToday = date.toDateString() === now.toDateString();
                          const hasSlotsToday =
                            isToday && selectedVenue
                              ? getAvailableTimeSlotsForDate(date, selectedVenue?.schedule, {
                                  requiredDurationMinutes: requiredDuration
                                }).length > 0
                              : true;
                          const noSlotsLeftToday = !blocked && isToday && !hasSlotsToday;
                          const isUnavailable = blocked || beforeBookingWindow || noSlotsLeftToday;
                          return (
                            <button
                              key={date.toISOString()}
                              disabled={isUnavailable}
                              title={
                                beforeBookingWindow
                                  ? 'Bookings cannot be in the past.'
                                  : blocked
                                    ? reason
                                    : noSlotsLeftToday
                                      ? 'No remaining slots today.'
                                      : reason
                              }
                              onClick={() =>
                                setBookingData((prev) => ({ ...prev, date, time: '' }))
                              }
                              className={`py-3 px-1 rounded-lg border text-center transition-all ${isSelected ? 'bg-teal-600 text-white border-teal-600' : 'bg-white hover:border-teal-400'} ${isUnavailable ? 'opacity-40 cursor-not-allowed bg-slate-100' : ''}`}
                            >
                              <div className="text-[10px] font-bold uppercase opacity-75">
                                {date.getDate()}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {bookingData.date && (
                      <div className="animate-fade-in">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-bold text-slate-700 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Select Available Time Slot
                          </h4>
                          <label className="flex items-center gap-2 text-[11px] font-bold text-slate-600">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                              checked={allowOverbook}
                              onChange={(e) => setAllowOverbook(e.target.checked)}
                            />
                            Allow overbook
                          </label>
                        </div>
                        {allowOverbook && (
                          <p className="text-[11px] text-red-500 mb-3">
                            Overbooking enabled: fully booked slots can be selected.
                          </p>
                        )}
                        {isLoadingAvailability ? (
                          <p className="text-sm text-slate-500 italic">Loading availability...</p>
                        ) : availableSlots.length === 0 ? (
                          <p className="text-sm text-slate-500 italic">
                            No opening hours defined for this day.
                          </p>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                            {availableSlots.map((time) => {
                              const availabilitySlot = availabilityByTime.get(time);
                              const slotStart = getMinutes(time);
                              const slotEnd = slotStart + requiredDuration;
                              const fallbackConflict = fallbackBusyIntervals.some(
                                (interval) => slotStart < interval.end && slotEnd > interval.start
                              );
                              const isConflict = availabilitySlot
                                ? !availabilitySlot.available
                                : fallbackConflict;

                              return (
                                <button
                                  key={time}
                                  disabled={!allowOverbook && isConflict}
                                  title={isConflict ? 'Slot is fully booked' : undefined}
                                  onClick={() => setBookingData({ ...bookingData, time })}
                                  className={`py-2 px-3 text-xs rounded-lg border transition-all font-medium ${bookingData.time === time ? 'bg-teal-600 text-white border-teal-600' : isConflict ? (allowOverbook ? 'bg-red-50 text-red-600 border-red-200 hover:border-red-300' : 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through') : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400'}`}
                                >
                                  {time}
                                  {availabilitySlot && availabilitySlot.capacity > 1
                                    ? ` (${availabilitySlot.remaining} left)`
                                    : ''}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {bookingData.time && (
                          <div className="mt-6 p-4 bg-teal-50 border border-teal-100 rounded-xl flex items-center justify-between">
                            <div>
                              <p className="text-xs text-teal-600 font-bold uppercase">
                                Selected Slot
                              </p>
                              <p className="text-teal-900 font-bold">
                                {bookingData.date.toLocaleDateString('en-GB', {
                                  dateStyle: 'full'
                                })}{' '}
                                at {bookingData.time}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-teal-600 font-bold uppercase">
                                Total Duration
                              </p>
                              <p className="text-teal-900 font-bold">{requiredDuration} mins</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <div className="flex-1">
            {error && (
              <p className="text-xs font-bold text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {error}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                disabled={isProcessing}
                className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg font-bold hover:bg-white flex items-center gap-2 text-sm disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Schedule
              </button>
            )}
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={!canContinueToBooking}
                className="bg-slate-900 text-white px-8 py-2 rounded-lg font-bold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                Patient Details →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || isProcessing}
                className={`px-10 py-2 rounded-lg font-bold transition-all text-sm flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${paymentMethod === 'card' ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Authorizing with Opayo...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {paymentMethod === 'card' ? 'Process Transaction' : 'Complete Booking'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        {threeDSData && (
          <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-800">3D Secure Verification</h4>
                <span className="text-xs text-slate-400">Complete the challenge to continue</span>
              </div>
              <iframe
                title="3DS Verification"
                name="opayo-3ds-iframe"
                className="w-full h-[70vh] border rounded-xl"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
